import { useRef, useState } from 'react';
import { MoreHorizontal, Archive, Zap, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { StatusPicker } from '../TaskDialog/StatusPicker';
import type { ProjectStatus } from '../../types';

interface KanbanColumnMenuProps {
  status: ProjectStatus;
  onDelete?: () => void;
  'data-testid'?: string;
}

export function KanbanColumnMenu({
  status,
  onDelete,
  'data-testid': testId,
}: KanbanColumnMenuProps) {
  const { t } = useTranslation('tasks');
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const openingPickerRef = useRef(false);

  function handleEditClick() {
    openingPickerRef.current = true;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6 opacity-100"
            data-testid={testId}
            aria-label={t('boardView.column.moreOptionsAria')}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          onCloseAutoFocus={(e) => {
            if (openingPickerRef.current) {
              e.preventDefault();
              openingPickerRef.current = false;
              setStatusPickerOpen(true);
            }
          }}
        >
          <DropdownMenuItem data-testid={`tasks-kanban-column-${status.id}-archive-all`} disabled>
            <Archive className="mr-2 size-4" />
            {t('boardView.column.archiveAll')}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid={`tasks-kanban-column-${status.id}-automate`} disabled>
            <Zap className="mr-2 size-4" />
            {t('boardView.column.automate')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`tasks-kanban-column-${status.id}-edit`}
            onClick={handleEditClick}
          >
            <Pencil className="mr-2 size-4" />
            {t('boardView.column.edit')}
          </DropdownMenuItem>
          {onDelete && (
            <DropdownMenuItem
              data-testid={`tasks-kanban-column-${status.id}-delete`}
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-2 size-4" />
              {t('boardView.column.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <StatusPicker
        boardId={status.boardId}
        open={statusPickerOpen}
        onOpenChange={setStatusPickerOpen}
        defaultEditStatusId={status.id}
        trigger={<span />}
      />
    </>
  );
}
